import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Send, Bot, User, X, FileText, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import AdDisplay from '../components/AdDisplay';

// ---- Utility: parse "[1]", "[2, 3]", "[1][2]" style markers in the answer
// and re-render them as clickable superscript links. Everything BETWEEN the
// markers is preserved as-is so line breaks / lists survive.
const renderAnswerWithCitations = (text, citations, onCitationClick) => {
  if (!text) return null;
  const cited = new Map(citations.map((c) => [c.idx, c]));
  const nodes = [];
  const regex = /\[(\d+(?:\s*,\s*\d+)*)\]/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      nodes.push(
        <span key={`t-${key++}`}>{text.slice(lastIdx, m.index)}</span>
      );
    }
    const nums = m[1].split(',').map((s) => Number(s.trim())).filter(Boolean);
    nums.forEach((n, i) => {
      const cit = cited.get(n);
      const cls = cit
        ? 'cursor-pointer text-primary hover:underline font-semibold'
        : 'text-slate-400 font-semibold';
      nodes.push(
        <sup
          key={`c-${key++}-${n}-${i}`}
          className={cls}
          data-testid={`chat-citation-sup-${n}`}
          onClick={cit ? () => onCitationClick(cit) : undefined}
          title={cit ? `View: ${cit.source_title}` : 'Source not found'}
        >
          [{n}]
        </sup>
      );
    });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    nodes.push(<span key={`t-${key++}`}>{text.slice(lastIdx)}</span>);
  }
  return nodes;
};

// Map a citation's `source_kind` + `source_id` to a deep-link URL. Documents
// and text-entries have no viewer route so we return null — the caller
// falls back to the snippet modal for those.
const deepLinkForCitation = (cit) => {
  if (!cit) return null;
  if (cit.source_kind === 'lesson' && cit.source_id) return `/lesson/${cit.source_id}`;
  if (cit.source_kind === 'forum_thread' && cit.source_id) return `/community/t/${cit.source_id}`;
  return null;
};

const kindLabel = (kind) => {
  if (kind === 'document') return 'Document';
  if (kind === 'lesson') return 'Lesson';
  if (kind === 'forum_thread') return 'Forum thread';
  if (kind === 'text_entry') return 'Knowledge entry';
  return kind || 'Source';
};

const SourceCard = ({ cit, onOpen }) => {
  const link = deepLinkForCitation(cit);
  const handleClick = () => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      onOpen(cit);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-left w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 hover:border-primary hover:bg-primary/5 transition-colors p-3"
      data-testid={`chat-source-card-${cit.idx}`}
      title={link ? `Open ${kindLabel(cit.source_kind)} in a new tab` : 'View source snippet'}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary text-primary-foreground text-[10px] font-bold shrink-0">
            {cit.idx}
          </span>
          <span className="font-semibold text-sm truncate">{cit.source_title}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 shrink-0">
          {(cit.score * 100).toFixed(0)}% match
        </span>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{cit.snippet}</p>
      <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide">
        <FileText className="w-3 h-3" />
        {kindLabel(cit.source_kind)} · {link ? 'Open source ↗' : 'View source'}
      </div>
    </button>
  );
};

const SourceModal = ({ cit, onClose }) => {
  if (!cit) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur"
      onClick={onClose}
      data-testid="chat-source-modal"
    >
      <div
        className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Source [{cit.idx}] · {(cit.score * 100).toFixed(0)}% match
            </div>
            <h3 className="text-lg font-bold mt-1">{cit.source_title}</h3>
            <div className="text-xs text-slate-500 mt-0.5">
              {cit.source_kind === 'document' ? 'Uploaded document' : 'Knowledge base entry'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-foreground"
            data-testid="chat-source-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
            {cit.snippet}
          </p>
        </div>
      </div>
    </div>
  );
};

const SESSION_STORAGE_KEY = 'fda_ai_chat_session';

const AIChat = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeSource, setActiveSource] = useState(null);

  // Persist session_id across page refreshes so the AI's memory of the
  // conversation survives. Only regenerate on explicit "New chat".
  const [sessionId, setSessionId] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) return saved;
    } catch { /* ignore */ }
    const fresh = Math.random().toString(36).substring(7);
    try { localStorage.setItem(SESSION_STORAGE_KEY, fresh); } catch { /* ignore */ }
    return fresh;
  });

  const messagesEndRef = useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => { scrollToBottom(); }, [messages]);

  // On mount: try to rehydrate the last conversation for this session so
  // a page refresh doesn't wipe visible history (the backend still has
  // it, but the user shouldn't have to re-scroll).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/history/${sessionId}`);
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setMessages(
            data.map((m) => ({
              role: m.role,
              content: m.content,
              citations: m.citations || [],
            }))
          );
          return;
        }
      } catch { /* endpoint may not exist; fall through to welcome */ }
      if (!cancelled) {
        setMessages([{
          role: 'assistant',
          content:
            "Hello! I'm your Freeze Dry Academy AI assistant. Ask me anything — cycles, moisture, recipes, business — and I'll cite the source material where I can. Follow up naturally; I'll remember what we've been talking about.",
          citations: [],
        }]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage, citations: [] }]);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
      });
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: response.data.response,
        citations: response.data.citations || [],
      }]);
    } catch (error) {
      toast.error('Failed to get response');
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        citations: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    const fresh = Math.random().toString(36).substring(7);
    try { localStorage.setItem(SESSION_STORAGE_KEY, fresh); } catch { /* ignore */ }
    setSessionId(fresh);
    setMessages([]);
    toast.success('Started a fresh conversation');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Bot className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold flex-1">AI Assistant</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={newChat}
            data-testid="chat-new-conversation"
            title="Start a fresh conversation — this clears the AI's memory of the current thread"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            New chat
          </Button>
        </div>
      </div>

      <ToolSelector />

      <div className="container mx-auto px-4 py-6">
        <PageTutorial id="ai_chat" />
        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card className="h-[calc(100vh-180px)] flex flex-col shadow-2xl">
              <CardHeader className="bg-navy text-background rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-6 h-6" />
                  Freeze Dry AI Assistant
                </CardTitle>
              </CardHeader>

              <CardContent className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
                {messages.map((msg, index) => (
                  <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-primary text-white'
                          : 'bg-white border border-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700'
                      }`}
                      data-testid={msg.role === 'user' ? 'user-message' : 'assistant-message'}
                    >
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {msg.role === 'assistant' && msg.citations?.length > 0
                          ? renderAnswerWithCitations(msg.content, msg.citations, setActiveSource)
                          : msg.content}
                      </div>

                      {msg.role === 'assistant' && msg.citations?.length > 0 ? (
                        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                            Sources cited ({msg.citations.length})
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2" data-testid={`chat-sources-${index}`}>
                            {msg.citations.map((c) => (
                              <SourceCard key={c.idx} cit={c} onOpen={setActiveSource} />
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              <div className="p-4 border-t">
                <form onSubmit={handleSend} className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about freeze-drying… (I remember earlier questions in this thread)"
                    disabled={loading}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button type="submit" disabled={loading || !input.trim()} data-testid="send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <AdDisplay location="aichat_sidebar" />
          </div>
        </div>
      </div>

      <SourceModal cit={activeSource} onClose={() => setActiveSource(null)} />
    </div>
  );
};

export default AIChat;
